import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Divider, Button, Group, Title, createStyles, Box, Image, ColorInput, Text, Paper } from '@mantine/core';
import TenantLogoDropzone from './TenantLogoDropzone';
import { notifications } from '@mantine/notifications';
import { updateAdminTenant } from '../../services/api.service';
import { IconCheck } from '@tabler/icons-react';
import { colors } from '../../lib/colors';
import { useSelector } from 'react-redux';
import useUserApi from '../../hooks/useUserApi';

// Custom styles
const useStyles = createStyles((theme) => ({
	imageContainer: {
		border: '1px solid #CCCCCC',
		padding: 3,
		borderRadius: theme.radius.sm,
	},
	fieldError: {
		fontFamily: 'Montserrat, sans-serif',
		color: '#FF8787',
		fontSize: 'calc(0.875rem - 0.125rem)',
		lineHeight: 1.2,
	},
	floatingContainer: {
		position: 'absolute',
		padding: theme.spacing.md,
		maxWidth: 600,
		height: 'auto',
		width: '100%',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)'
	}
}));

const SettingsForm = () => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [fileURL, setFileURL] = useState('');
	const [fileIsotypeURL, setFileIsotypeURL] = useState('');
	const [isLogoChanged, toggleIsLogoChanged] = useState(false);
	const [isIsotypeChanged, toggleIsIsotypeChanged] = useState(false);
	const { classes } = useStyles();
	const tenant = useSelector(state => state.authData.tenant);
	const { verifyJWT } = useUserApi();
	const form = useForm({
		initialValues: {
			name: tenant?.name,
			referalCode: tenant?.referalCode,
			primaryColor: tenant?.primaryColor,
			secondaryColor: tenant?.secondaryColor,
			tertiaryColor: tenant?.tertiaryColor,
			logo: tenant?.logo,
			isotype: tenant?.isotype,
			active: true,
		},
		validate: {
			referalCode: (value) => value !== '' ? null : 'El código de referido es requerido.',
			primaryColor: (value) => value !== '' ? null : 'Debes elegir un color primario.',
			logo: (value) => value ? null : 'Debes agregar un logotipo.',
			isotype: (value) => value ? null : 'Debes agregar un isotipo.',
		},
	});

	// Listen to tenant changes
	useEffect(() => {
		form.setValues({
			name: tenant?.name,
			referalCode: tenant?.referalCode,
			primaryColor: tenant?.primaryColor,
			secondaryColor: tenant?.secondaryColor,
			tertiaryColor: tenant?.tertiaryColor,
			logo: tenant?.logo,
			isotype: tenant?.isotype,
			active: true,
		});
		// eslint-disable-next-line
	}, [tenant]);

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Call API
		updateAdminTenant(tenant.id, values, async function () {

			// Show success
			notifications.show({
				title: 'Configuración guardada correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Get JWT again
			await verifyJWT();

			// Remove preloader
			toggleLoading(false);

		}, function (error) {

			// Remove preloader
			toggleLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// Process accepted file
	const onDropLogo = (file) => {
		form.setFieldValue('logo', file[0]);
		setFileURL(URL.createObjectURL(file[0]));
		toggleIsLogoChanged(true);
	}

	// Process accepted file
	const onDropIsotype = (file) => {
		form.setFieldValue('isotype', file[0]);
		setFileIsotypeURL(URL.createObjectURL(file[0]));
		toggleIsIsotypeChanged(true);
	}

	// Reject file and show error
	const onReject = (file) => {
		notifications.show({
			title: 'Error',
			message: 'Este tipo de archivo no está soportado.',
			color: 'red'
		});
	}

	// On primary color change
	useEffect(() => {

		// If color selected
		if (form.values.primaryColor) {

			// Assign secondary and tertiary
			form.setFieldValue('secondaryColor', colors[form.values.primaryColor]?.secondary);
			form.setFieldValue('tertiaryColor', colors[form.values.primaryColor]?.tertiary);

		}

		// eslint-disable-next-line
	}, [form.values.primaryColor]);

	return (
		<Paper
			className={classes.floatingContainer}
			shadow="xs"
		>
			{/* Form */}
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Input Group */}
				<Group grow>
					{/* Referal Code */}
					<TextInput
						withAsterisk
						label="Código de referido"
						placeholder="Escribe un código de referido"
						value={form.values.referalCode}
						onChange={(event) => form.setFieldValue('referalCode', event.currentTarget.value)}
						{...form.getInputProps('referalCode')}
					/>
					{/* Color */}
					<ColorInput
						withAsterisk
						placeholder="Elige un color"
						label="Color primario"
						disallowInput
						withPicker={false}
						withEyeDropper={false}
						value={form.values.primaryColor}
						onChange={(event) => form.setFieldValue('primaryColor', event.currentTarget.value)}
						{...form.getInputProps('primaryColor')}
						swatches={[
							'#EF5820',
							'#FF5E04',
							'#F1780E',
							'#E9DE0A',
							'#FFDF1F',
							'#F3B605',
							'#1E52A4',
							'#066FAD',
							'#2AA9D3',
							'#D80122',
							'#EB001B',
							'#FF2A42',
							'#F3A2B2',
							'#813D88',
							'#610DF4',
							'#D2B8F8',
							'#18704B',
							'#8DBE09',
							'#21D863',
						]}
					/>
				</Group>
				<Divider my="md" />
				{/* Logo */}
				<Box>
					{/* Subtitle */}
					<Title
						order={5}
						mb="md"
					>
						Logotipo
					</Title>
					{/* Dropzone */}
					<Group noWrap>
						{/* Zone */}
						<TenantLogoDropzone
							text="Sube el logotipo de este partner"
							onDrop={onDropLogo}
							onReject={onReject}
						/>
						{/* Error */}
						{form.errors?.logo && (
							<Text className={classes.fieldError}>
								{form.errors?.logo.toString()}
							</Text>
						)}
						{/* Preview */}
						{form.values.logo !== null ?
							<Box className={classes.imageContainer}>
								<Image
									withPlaceholder
									src={!isLogoChanged ? tenant.logo : fileURL}
									imageProps={!isLogoChanged ? null : { onLoad: () => URL.revokeObjectURL(fileURL) }}
									height={148}
									width={148}
									radius="sm"
								/>
							</Box>
						: null}
					</Group>
				</Box>
				{/* Isotype */}
				<Box mt="md">
					{/* Subtitle */}
					<Title
						order={5}
						mb="md"
					>
						Isotipo
					</Title>
					{/* Dropzone */}
					<Group noWrap>
						{/* Zone */}
						<TenantLogoDropzone
							text="Sube el isotipo de este partner"
							onDrop={onDropIsotype}
							onReject={onReject}
						/>
						{/* Error */}
						{form.errors?.isotype && (
							<Text className={classes.fieldError}>
								{form.errors?.isotype.toString()}
							</Text>
						)}
						{/* Preview */}
						{form.values.isotype !== null ?
							<Box className={classes.imageContainer}>
								<Image
									withPlaceholder
									src={!isIsotypeChanged ? tenant.isotype : fileIsotypeURL}
									imageProps={!isIsotypeChanged ? null : { onLoad: () => URL.revokeObjectURL(fileIsotypeURL) }}
									height={148}
									width={148}
									radius="sm"
								/>
							</Box>
						: null}
					</Group>
				</Box>
				{/* Buttons */}
				<Group position="apart">
					{/* Cancel */}
					&nbsp;
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
					>
						Guardar Cambios
					</Button>
				</Group>
			</form>
		</Paper>
	);

}

export default SettingsForm;
